import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import Logo from "../../assets/MNR.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  const closeMobile = () => setIsMobileMenuOpen(false);

  const navLinks = [
    { name: "Home", path: "/", num: "01" },
    { name: "About", path: "/about-us", num: "02" },
    { name: "Services", path: "/our-services", num: "03" },
    { name: "Contact", path: "/contact-us", num: "04" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

        :root {
          --orange: #E78730;
          --nav-height: 72px;
        }

        .nav-root {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          height: var(--nav-height);
          display: flex;
          align-items: center;
          transition: background 0.4s ease, border-color 0.4s ease;
          border-bottom: 1px solid transparent;
          font-family: 'DM Sans', sans-serif;
        }

        .nav-root.scrolled {
          background: rgba(10, 10, 10, 0.92);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom-color: rgba(255,255,255,0.08);
        }

        .nav-inner {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 5vw;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }

        /* ── Logo ── */
        .nav-logo {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          text-decoration: none;
          flex-shrink: 0;
          z-index: 1;
        }

        .nav-logo img {
          height: 40px;
          width: auto;
          transition: opacity 0.2s;
        }
        .nav-logo:hover img { opacity: 0.85; }

        /* ── Center links ── */
        .nav-links {
          display: flex;
          align-items: center;
          list-style: none;
          gap: 0;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }

        .nav-link-item a {
          position: relative;
          display: block;
          padding: 0.5rem 1.25rem;
          font-size: 0.72rem;
          font-weight: 400;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          text-decoration: none;
          color: rgba(255,255,255,0.45);
          transition: color 0.25s;
          font-family: 'DM Sans', sans-serif;
        }

        .nav-link-item a::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 1.25rem;
          right: 1.25rem;
          height: 1px;
          background: var(--orange);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .nav-link-item a:hover,
        .nav-link-item a.active {
          color: #fff;
        }

        .nav-link-item a:hover::after,
        .nav-link-item a.active::after {
          transform: scaleX(1);
        }

        /* ── CTA ── */
        .nav-cta {
          z-index: 1;
          flex-shrink: 0;
        }

        .nav-cta-btn {
          display: inline-block;
          background: var(--orange);
          color: #fff;
          font-size: 0.7rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          padding: 0.65rem 1.5rem;
          font-family: 'DM Sans', sans-serif;
          transition: opacity 0.2s;
          line-height: 1;
        }
        .nav-cta-btn:hover { opacity: 0.82; }

        /* ── Mobile toggle ── */
        .nav-mobile-btn {
          display: none;
          background: none;
          border: 1px solid rgba(255,255,255,0.18);
          padding: 0.45rem 0.55rem;
          cursor: pointer;
          color: #fff;
          z-index: 1;
          transition: border-color 0.2s;
          align-items: center;
          justify-content: center;
        }
        .nav-mobile-btn:hover { border-color: rgba(255,255,255,0.45); }

        /* ── Mobile menu ── */
        .mobile-menu {
          display: none;
          position: fixed;
          top: var(--nav-height);
          left: 0; right: 0; bottom: 0;
          background: rgba(10,10,10,0.97);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-top: 1px solid rgba(255,255,255,0.07);
          padding: 2.5rem 5vw 4rem;
          z-index: 99;
          flex-direction: column;
          font-family: 'DM Sans', sans-serif;
          overflow-y: auto;
        }

        .mobile-menu.open { display: flex; }

        .mobile-nav-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          text-decoration: none;
          font-size: clamp(1.75rem, 6vw, 2.5rem);
          font-family: 'DM Serif Display', serif;
          color: rgba(255,255,255,0.45);
          transition: color 0.2s;
        }

        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          color: #fff;
        }

        .mobile-link-num {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--orange);
          font-weight: 500;
        }

        .mobile-cta-btn {
          margin-top: 2.5rem;
          display: inline-block;
          align-self: flex-start;
          background: var(--orange);
          color: #fff;
          font-size: 0.72rem;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          padding: 1rem 2.25rem;
          font-family: 'DM Sans', sans-serif;
          transition: opacity 0.2s;
        }
        .mobile-cta-btn:hover { opacity: 0.82; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .nav-links  { display: none; }
          .nav-cta    { display: none; }
          .nav-mobile-btn { display: flex; }
        }
      `}</style>

      {/* ── Main navbar ── */}
      <nav className={`nav-root${isScrolled ? " scrolled" : ""}`}>
        <div className="nav-inner">
          {/* Logo */}
          <NavLink to="/" className="nav-logo" onClick={closeMobile}>
            <img src={Logo} alt="Trademin Logo" />
          </NavLink>

          {/* Desktop center links */}
          <ul className="nav-links">
            {navLinks.map((item) => (
              <li key={item.name} className="nav-link-item">
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Desktop CTA */}
          <div className="nav-cta">
            <NavLink to="/request-quote" className="nav-cta-btn">
              Get Quote
            </NavLink>
          </div>

          {/* Mobile toggle */}
          <button
            className="nav-mobile-btn"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* ── Mobile menu ── */}
      <div className={`mobile-menu${isMobileMenuOpen ? " open" : ""}`}>
        {navLinks.map((item) => (
          <NavLink
            key={item.name}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              `mobile-nav-link${isActive ? " active" : ""}`
            }
            onClick={closeMobile}
          >
            <span>{item.name}</span>
            <span className="mobile-link-num">{item.num}</span>
          </NavLink>
        ))}
        <NavLink
          to="/request-quote"
          className="mobile-cta-btn"
          onClick={closeMobile}
        >
          Get Quote →
        </NavLink>
      </div>
    </>
  );
};

export default Navbar;
